/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import Table from "components/Table";
import ContentShell from "components/ContentShell";
import {
  Box,
  IconButton,
  MenuItem,
  Menu,
  Switch,
  FormControlLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchInput from "components/SearchInput";
import { makeStyles } from "@mui/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "hooks/useSnackbar";
import configData from "config/config.json";
import { operatorTableColumns } from "utils/tableHelper/pobTableHelper";
import {
  deleteOperator,
  getOperators,
  updateOperatorStatus,
  toggleGlobalMaintenance,
  permanentDeleteOperator,
} from "services/operatorService";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import CustomButton from "components/CustomButton";
import { defaultPageNo, defaultPageSize } from "utils/constants";
import { sortArray } from "utils/miscUtils";
import useConsent from "hooks/useConsent";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import GlobalMaintenanceConfirmDialog from "./components/GlobalMaintenanceConfirmDialog";
import sessionHelperInstance from "services/sessionHelper";

const useStyles = makeStyles((theme: any) => ({
  playerCardParent: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
  },
}));

interface RowActionsProps {
  rowId?: any;
  data: any;
  actionHandlers: {
    handleDelete?: (data: any) => void;
    handleDisable?: (id: any) => void;
    handlePermanentDelete?: (id: any) => void;
  };
}

const RowActions = ({ rowId, data, actionHandlers }: RowActionsProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const { handleDelete = () => {}, handleDisable = () => {}, handlePermanentDelete = () => {} } = actionHandlers;
  const onEdit = () =>
    navigate(configData.routes.operators.edit?.replace(":id", data?._id));

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.OPERATOR.DELETE_ROLES
  );

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={onEdit}>Edit</MenuItem>
        <MenuItem onClick={() => handleDisable(data?._id)}>
          {data.isActive ? "Disable" : "Enable"}
        </MenuItem>
        {hasDeletePermission && (
          <>
            <MenuItem onClick={() => handleDelete(data)}>Delete</MenuItem>
            <MenuItem onClick={() => handlePermanentDelete(data)}>
              Permanent Delete
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

const Operators = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  // Single source of truth for URL params - shared across the component
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [orderDirection, setOrderDirection] = useState("asc");

  // Global maintenance state
  const [globalMaintenanceEnabled, setGlobalMaintenanceEnabled] =
    useState(false);
  const [globalMaintenanceLoading, setGlobalMaintenanceLoading] =
    useState(false);
  const [globalMaintenanceDialogOpen, setGlobalMaintenanceDialogOpen] =
    useState(false);

  // Read search value from URL params directly
  const searchValue = searchParams.get("search") || "";

  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

   const {
    isOpenConsent: isOpenPermanentDeleteConsent,
    constentDetails: permanentDeleteConsentDetails,
    openConsent: openPermanentDeleteConsent,
    closeConsent: closePermanentDeleteConsent,
    updateDetails: updatePermanentDeleteDetails,
  } = useConsent();
  
  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.PLATFORM.UPDATE_ROLES
  );

  // Read pagination values from URL params directly
  const pageNo = Number(searchParams.get("pageNo")) || defaultPageNo;
  const pageSize = Number(searchParams.get("pageSize")) || defaultPageSize;

  // Unified pagination handlers using single setSearchParams
  const onChangePage = (_pageNo: number) => {
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("pageNo", String(_pageNo));
      return updatedParams;
    });
  };

  const onPageSizeChange = (event: any) => {
    const newPageSize = Number(event?.target?.value ?? defaultPageSize);
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("pageNo", "1");
      updatedParams.set("pageSize", String(newPageSize));
      return updatedParams;
    });
  };

  const onColumnClick = (column: any) => {
    let _list = sortArray([...filteredList], orderDirection, column);
    setFilteredList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const fetchData = async (search?: any) => {
    setLoading(true);
    try {
      let payload = { pageNo, pageSize };
      if (searchValue) payload["search"] = searchValue;
      const resp = await getOperators(payload);
      const operators = resp?.data?.operators || [];
      setFilteredList(
        operators.map((el) => ({
          ...el,
          coinPurchaseAllowedText: el?.coinPurchaseAllowed
            ? "Allowed"
            : "Not Allowed",
          isActiveText: el?.isActive ? "YES" : "NO",
          isUnderMaintenance: el?.isUnderMaintenance ? "Yes" : "No",
          rowDisabled: !el?.isActive,
        }))
      );
      // Get global maintenance status from the response
      setGlobalMaintenanceEnabled(resp?.data?.globalEnabled ?? false);
      setLoading(false);
    } catch (error) {
      showSnackbar(error.message);
      setLoading(false);
    }
  };

  const updateOperatorDropdownOptions = async () => {
    let payload = { skipPagination: true };
    const resp = await getOperators(payload);
    const operators = resp?.data?.operators || [];
    const updatedOperatorDropdownOptions = operators
      .map((op) => ({
        id: op?._id,
        platform: op?.platform,
        label: op?.operatorName,
      }))
      .sort((a: any, b: any) =>
        String(a?.label ?? "").localeCompare(String(b?.label ?? "")),
      );
    sessionHelperInstance.updatePBO(updatedOperatorDropdownOptions, "operator");
  };

  const handleGlobalMaintenanceToggleClick = () => {
    setGlobalMaintenanceDialogOpen(true);
  };

  const handleGlobalMaintenanceConfirm = async () => {
    setGlobalMaintenanceLoading(true);
    try {
      const newValue = !globalMaintenanceEnabled;
      const resp = await toggleGlobalMaintenance(newValue);
      setGlobalMaintenanceEnabled(resp?.data?.globalEnabled ?? newValue);
      showSnackbar(
        resp?.data?.message ||
          `Global maintenance ${newValue ? "enabled" : "disabled"}`
      );
      setGlobalMaintenanceDialogOpen(false);
      // Refresh the operators list to reflect changes
      fetchData(searchValue);
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setGlobalMaintenanceLoading(false);
    }
  };

  const handleGlobalMaintenanceDialogClose = () => {
    if (!globalMaintenanceLoading) {
      setGlobalMaintenanceDialogOpen(false);
    }
  };

  const onSearch = (value: any) => {
    // Update both search and pageNo in URL params together atomically
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      if (value) {
        updatedParams.set("search", value);
      } else {
        updatedParams.delete("search");
      }
      updatedParams.set("pageNo", String(defaultPageNo));
      return updatedParams;
    });
  };

  const createOperator = () => navigate(configData.routes.operators.create);

  const _openConsentDialog = (data: any) => {
    openConsent();
    updateDetails({ itemName: data.operatorName, id: data._id });
  };

  const _openPermanentDeleteConsentDialog = (data: any) => {
    openPermanentDeleteConsent();
    updatePermanentDeleteDetails({ itemName: data.operatorName, id: data._id });
  };

  const onDelete = async () => {
    try {
      const resp = await deleteOperator(constentDetails.id);
      showSnackbar(resp?.data?.message);
      fetchData(searchValue);
      updateOperatorDropdownOptions();
      closeConsent();
    } catch (error) {
      closeConsent();
      showSnackbar(error.message, "error");
    }
  };

    const onPermanentDelete = async () => {
    try {
      const resp = await permanentDeleteOperator(permanentDeleteConsentDetails.id);
      showSnackbar(resp?.data?.message);
      fetchData(searchValue);
      updateOperatorDropdownOptions();
      closePermanentDeleteConsent();
    } catch (error) {
      closePermanentDeleteConsent();
      showSnackbar(error.message, "error");
    }
  };

  const onDisable = async (id) => {
    try {
      const resp = await updateOperatorStatus(id);
      showSnackbar(resp?.data?.message);
      fetchData(searchValue);
    } catch (error) {
      showSnackbar(error.message);
    }
  };

  useEffect(() => {
    const initialColumns = operatorTableColumns().filter(
      (col) => col.id !== "actions" || hasEditPermission
    );
    setColumns(initialColumns);

    fetchData(searchValue);
  }, [pageNo, pageSize, searchValue]);

  return (
    <ContentShell
      title="Operators"
      loading={loading}
      actions={
        <Box
          style={{
            display: "flex",
            flexFlow: "row nowrap",
            gap: 8,
            alignItems: "center",
          }}
        >
          {/* Global Maintenance Toggle */}
          {hasEditPermission && (
            <Tooltip
              title={
                globalMaintenanceEnabled
                  ? "Global maintenance is currently enabled. Click to disable."
                  : "Enable global maintenance for all active operators."
              }
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: globalMaintenanceEnabled
                    ? "#ffebee"
                    : "#e8f5e9",
                  borderRadius: "8px",
                  padding: "4px 12px",
                  border: globalMaintenanceEnabled
                    ? "1px solid #f44336"
                    : "1px solid #4caf50",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    color: globalMaintenanceEnabled ? "#d32f2f" : "#2e7d32",
                    mr: 1,
                  }}
                >
                  Global Maintenance
                </Typography>
                <Switch
                  checked={globalMaintenanceEnabled}
                  onChange={handleGlobalMaintenanceToggleClick}
                  color={globalMaintenanceEnabled ? "error" : "success"}
                  size="small"
                />
              </Box>
            </Tooltip>
          )}
          <SearchInput
            value={searchValue}
            placeholder="Search an operator"
            onSearch={onSearch}
          />
          {sessionHelper.hasRole(PermittedRoles.LEAGUE.CREATE_ROLES) && (
            <CustomButton onClick={createOperator}>Create</CustomButton>
          )}
        </Box>
      }
    >
      <Box className={styles.playerCardParent}>
        <Table
          columns={columns}
          data={filteredList}
          currentPage={pageNo}
          actions={RowActions}
          pageSize={pageSize}
          totalEntries={filteredList.length}
          onChangePage={onChangePage}
          onColumnClick={onColumnClick}
          onRowsPerPageChange={onPageSizeChange}
          highlightAltRow={true}
          actionHandlers={{
            handleDisable: onDisable,
            handleDelete: _openConsentDialog,
            handlePermanentDelete: _openPermanentDeleteConsentDialog,
          }}
        />
        <DeleteConsentDialog
          itemName={constentDetails.itemName}
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={onDelete}
        />

        <DeleteConsentDialog
          itemName={permanentDeleteConsentDetails.itemName}
          open={isOpenPermanentDeleteConsent}
          onClose={closePermanentDeleteConsent}
          onConfirm={onPermanentDelete}
          btnText="Permanent Delete"
          message="Are you sure you want to permanently delete "
        />

        <GlobalMaintenanceConfirmDialog
          open={globalMaintenanceDialogOpen}
          onClose={handleGlobalMaintenanceDialogClose}
          onConfirm={handleGlobalMaintenanceConfirm}
          isCurrentlyEnabled={globalMaintenanceEnabled}
          loading={globalMaintenanceLoading}
        />
      </Box>
    </ContentShell>
  );
};

export default Operators;
